import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useSelector } from "react-redux";
import api from "../../services/api";
import { colors } from "./constants";
import CarryForwardTable from "./Carryforwardtable";
import CarryForwardDecisionDialog from "./Carryforwarddecisiondialog";

// ---- shared palette (matches PatientsComponent.js) -----------------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  slate: "#6B7280",
  line: "#E6E9ED",
};

function a11yProps(index) {
  return {
    id: `carry-forward-tab-${index}`,
    "aria-controls": `carry-forward-tabpanel-${index}`,
    className: "branch_tab",
  };
}

export default function CarryForwardPage({ onBack }) {
  const branches = useSelector((state) => state.userDetails.detail?.branches);

  const [tabIndex, setTabIndex] = useState(0);
  const branchId = branches?.[tabIndex]?.id;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const getCarryForwardSessions = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const response = await api.get("/app/patient/carry-forward/sessions", {
        params: { branch_id: branchId },
      });
      setSessions(response?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Unable to load carry-forward sessions.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    getCarryForwardSessions();
  }, [getCarryForwardSessions]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return sessions;
    const value = search.toLowerCase();
    return sessions.filter(
      (item) =>
        item.patient_name?.toLowerCase().includes(value) ||
        item.service_name?.toLowerCase().includes(value) ||
        item.remarks?.toLowerCase().includes(value),
    );
  }, [sessions, search]);

  const openDecision = (session) => {
    setSelectedSession(session);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedSession(null);
  };

  const handleDecisionSuccess = (sessionId, updated, decision) => {
    setSessions((previous) =>
      previous.map((item) =>
        item.id === sessionId
          ? { ...item, ...updated, status: updated?.status || decision }
          : item,
      ),
    );
    setSnackbar({
      open: true,
      message:
        decision === "MAKEUP"
          ? "Makeup session scheduled successfully."
          : decision === "ADJUSTMENT"
          ? "Session adjusted successfully."
          : "Carry-forward session rejected.",
      severity: "success",
    });
    closeDialog();
  };

  const handleDecisionError = (message) => {
    setSnackbar({ open: true, message, severity: "error" });
  };

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: colors.background, p: 2.5 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${colors.border}`,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <ArrowBackRoundedIcon />
            </IconButton>
          )}

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.text }}>
              Carry-Forward Sessions
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.secondary }}>
              Review and decide missed sessions
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search patient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 250, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: colors.secondary }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Refresh">
            <IconButton onClick={getCarryForwardSessions}>
              <RefreshRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ borderTop: `1px solid ${palette.line}` }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="branch tabs"
            TabIndicatorProps={{
              sx: { backgroundColor: palette.teal, height: "3px", borderRadius: "3px 3px 0 0" },
            }}
            sx={{
              minHeight: "42px",
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13.5px",
                color: palette.slate,
                minHeight: "42px",
                padding: "8px 18px",
                "&:hover": { color: palette.tealDark },
              },
              "& .Mui-selected": {
                color: `${palette.teal} !important`,
              },
            }}
          >
            {branches?.map((b, index) => (
              <Tab key={b.id} label={b.name} {...a11yProps(index)} className="branch_name" />
            ))}
          </Tabs>
        </Box>
      </Paper>

      <CarryForwardTable
        rows={filteredSessions}
        loading={loading}
        searching={Boolean(search.trim())}
        onDecide={openDecision}
      />

      <CarryForwardDecisionDialog
        open={dialogOpen}
        session={selectedSession}
        branchId={branchId}
        onClose={closeDialog}
        onSuccess={handleDecisionSuccess}
        onError={handleDecisionError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}